import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import GlassCard from '../GlassCard';
import { familiesAPI, peopleAPI, relationshipsAPI } from '../../services/api';
import SearchablePersonSelect from '../SearchablePersonSelect';

const RelationManagement = () => {
  const { theme } = useTheme();
  const [families, setFamilies] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [members, setMembers] = useState([]);
  const [parentChildRelations, setParentChildRelations] = useState([]);
  const [partnerships, setPartnerships] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingType, setEditingType] = useState(null); // 'parent-child' or 'partnership'
  const [editForm, setEditForm] = useState({});
  const [originalRelation, setOriginalRelation] = useState(null); // Store original relation for updates
  const [newRelation, setNewRelation] = useState({ type: 'parent-child' }); // 'parent-child' or 'partnership'
  const [searchTerm, setSearchTerm] = useState('');
  const [relationshipFilter, setRelationshipFilter] = useState('all'); // 'all', 'parent-child', 'spouse', 'partner'

  useEffect(() => {
    loadFamilies();
  }, []);

  useEffect(() => {
    if (selectedFamily) {
      loadMembers(selectedFamily);
      loadRelationships(selectedFamily);
    } else {
      setMembers([]);
      setParentChildRelations([]);
      setPartnerships([]);
    }
  }, [selectedFamily]);

  const loadFamilies = async () => {
    try {
      const response = await familiesAPI.list();
      setFamilies(response.data.families || []);
      if (response.data.families?.length > 0 && !selectedFamily) {
        setSelectedFamily(response.data.families[0].id);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load families');
    }
  };

  const loadMembers = async (familyId) => {
    try {
      const response = await peopleAPI.list(familyId);
      setMembers(response.data.people || []);
    } catch (err) {
      console.error('Failed to load members:', err);
    }
  };

  const loadRelationships = async (familyId) => {
    try {
      setLoading(true);
      setError('');
      
      const [parentChildRes, partnershipsRes] = await Promise.all([
        relationshipsAPI.parentChild.list(familyId),
        relationshipsAPI.partnerships.list(familyId),
      ]);
      
      setParentChildRelations(parentChildRes.data.parent_child || []);
      setPartnerships(partnershipsRes.data.partnerships || []);
    } catch (err) {
      console.error('Failed to load relationships:', err);
      setError(err.response?.data?.error || 'Failed to load relationships');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewRelation = () => {
    setNewRelation({ type: 'parent-child' });
    setEditingId('new');
    setEditingType('parent-child');
    setEditForm({
      type: 'parent-child',
      parent_person_id: '',
      child_person_id: '',
      kind: 'biological',
      person_id_1: '',
      person_id_2: '',
      partnership_type: 'spouse',
      start_date: '',
      end_date: '',
    });
  };

  const handleEdit = (relation, type) => {
    const relationId = relation.id || `${relation.parent_person_id}-${relation.child_person_id}`;
    setEditingId(relationId);
    setEditingType(type);
    setOriginalRelation(relation); // Store original for reference
    
    if (type === 'parent-child') {
      setEditForm({
        type: 'parent-child',
        parent_person_id: relation.parent_person_id,
        child_person_id: relation.child_person_id,
        kind: relation.kind || 'biological',
      });
    } else {
      setEditForm({
        type: 'partnership',
        person_id_1: relation.person_id_1,
        person_id_2: relation.person_id_2,
        partnership_type: relation.partnership_type || 'spouse',
        start_date: relation.start_date || '',
        end_date: relation.end_date || '',
      });
    }
  };

  const handleSave = async () => {
    try {
      setError('');
      if (editingId === 'new') {
        // Create new relationship
        if (editForm.type === 'parent-child') {
          if (!editForm.parent_person_id || !editForm.child_person_id) {
            setError('Both parent and child must be selected');
            return;
          }
          await relationshipsAPI.parentChild.create(selectedFamily, {
            parent_person_id: editForm.parent_person_id,
            child_person_id: editForm.child_person_id,
            kind: editForm.kind || 'biological',
          });
        } else {
          if (!editForm.person_id_1 || !editForm.person_id_2) {
            setError('Both persons must be selected');
            return;
          }
          await relationshipsAPI.partnerships.create(selectedFamily, {
            person_id_1: editForm.person_id_1,
            person_id_2: editForm.person_id_2,
            partnership_type: editForm.partnership_type || 'spouse',
            start_date: editForm.start_date || null,
            end_date: editForm.end_date || null,
          });
        }
      } else {
        // Update existing relationship
        if (editForm.type === 'parent-child') {
          await relationshipsAPI.parentChild.update(
            selectedFamily,
            editForm.parent_person_id,
            editForm.child_person_id,
            { kind: editForm.kind }
          );
        } else {
          await relationshipsAPI.partnerships.update(editingId, {
            partnership_type: editForm.partnership_type,
            start_date: editForm.start_date || null,
            end_date: editForm.end_date || null,
          });
        }
      }
      
      setEditingId(null);
      setEditingType(null);
      setEditForm({});
      setOriginalRelation(null);
      await loadRelationships(selectedFamily);
    } catch (err) {
      console.error('Failed to save relationship:', err);
      setError(err.response?.data?.error || 'Failed to save relationship');
    }
  };

  const handleDelete = async (relation, type) => {
    if (!window.confirm('Are you sure you want to delete this relationship?')) {
      return;
    }

    try {
      setError('');
      if (type === 'parent-child') {
        await relationshipsAPI.parentChild.delete(
          selectedFamily,
          relation.parent_person_id,
          relation.child_person_id
        );
      } else {
        await relationshipsAPI.partnerships.delete(relation.id);
      }
      await loadRelationships(selectedFamily);
    } catch (err) {
      console.error('Failed to delete relationship:', err);
      setError(err.response?.data?.error || 'Failed to delete relationship');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditingType(null);
    setEditForm({});
    setOriginalRelation(null);
    setError('');
  };

  const getPersonName = (personId) => {
    const person = members.find(p => p.id === personId);
    if (!person) return personId;
    return `${person.preferred_name || person.first_name || ''} ${person.last_name || ''}`.trim() || personId;
  };

  const getPerson = (personId) => {
    return members.find(p => p.id === personId);
  };

  const getParentRole = (personId) => {
    const person = getPerson(personId);
    if (!person) return 'Unknown';
    const sex = person.sex || 'unknown';
    if (sex === 'male') return 'Father';
    if (sex === 'female') return 'Mother';
    return 'Parent (Sex Unknown)';
  };

  const getParentRoleIcon = (personId) => {
    const person = getPerson(personId);
    if (!person) return '❓';
    const sex = person.sex || 'unknown';
    if (sex === 'male') return '👨';
    if (sex === 'female') return '👩';
    return '👤';
  };

  const allRelationships = [
    ...parentChildRelations.map(r => ({ ...r, _type: 'parent-child', _id: `${r.parent_person_id}-${r.child_person_id}` })),
    ...partnerships.map(r => ({ ...r, _type: 'partnership', _id: r.id })),
  ];

  // Filter relationships based on search term and relationship type filter
  const filteredRelationships = allRelationships.filter((relation) => {
    // First apply relationship type filter
    if (relationshipFilter === 'parent-child') {
      if (relation._type !== 'parent-child') return false;
    } else if (relationshipFilter === 'spouse') {
      if (relation._type !== 'partnership' || relation.partnership_type !== 'spouse') return false;
    } else if (relationshipFilter === 'partner') {
      if (relation._type !== 'partnership' || relation.partnership_type !== 'partner') return false;
    }
    // 'all' shows everything, so no filter needed
    
    // Then apply search term filter
    if (!searchTerm.trim()) return true;
    const searchLower = searchTerm.toLowerCase();
    
    if (relation._type === 'parent-child') {
      const parentName = getPersonName(relation.parent_person_id).toLowerCase();
      const childName = getPersonName(relation.child_person_id).toLowerCase();
      const kind = (relation.kind || '').toLowerCase();
      return parentName.includes(searchLower) || childName.includes(searchLower) || kind.includes(searchLower);
    } else {
      const person1Name = getPersonName(relation.person_id_1).toLowerCase();
      const person2Name = getPersonName(relation.person_id_2).toLowerCase();
      const partnershipType = (relation.partnership_type || '').toLowerCase();
      return person1Name.includes(searchLower) || person2Name.includes(searchLower) || partnershipType.includes(searchLower);
    }
  });

  // Calculate statistics
  const stats = {
    total: allRelationships.length,
    parentChild: parentChildRelations.length,
    spouse: partnerships.filter(p => p.partnership_type === 'spouse').length,
    partner: partnerships.filter(p => p.partnership_type === 'partner').length,
  };

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: 'clamp(20px, 4vw, 24px)', margin: '0 0 16px 0', color: theme.text.primary }}>
          🔗 Relation Management
        </h2>
        <p style={{ color: theme.text.secondary, fontSize: '14px', marginBottom: '16px' }}>
          Manage relationships between family members. Add parent-child relationships, spouse relationships, or partnerships.
        </p>

        {/* Relationship Statistics */}
        {selectedFamily && allRelationships.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '12px',
            marginBottom: '20px',
          }}>
            <GlassCard style={{ padding: '16px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.accent.primary, marginBottom: '4px' }}>
                {stats.total}
              </div>
              <div style={{ color: theme.text.secondary, fontSize: '12px' }}>Total</div>
            </GlassCard>
            <GlassCard style={{ padding: '16px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4caf50', marginBottom: '4px' }}>
                {stats.parentChild}
              </div>
              <div style={{ color: theme.text.secondary, fontSize: '12px' }}>Parent-Child</div>
            </GlassCard>
            <GlassCard style={{ padding: '16px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff6b6b', marginBottom: '4px' }}>
                {stats.spouse}
              </div>
              <div style={{ color: theme.text.secondary, fontSize: '12px' }}>Spouse</div>
            </GlassCard>
            <GlassCard style={{ padding: '16px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#9b59b6', marginBottom: '4px' }}>
                {stats.partner}
              </div>
              <div style={{ color: theme.text.secondary, fontSize: '12px' }}>Partner</div>
            </GlassCard>
          </div>
        )}
        
        {/* Family Selector */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: theme.text.secondary, 
            fontSize: '14px',
            fontWeight: 500,
          }}>
            Select Family
          </label>
          <select
            value={selectedFamily || ''}
            onChange={(e) => setSelectedFamily(e.target.value)}
            style={{
              width: '100%',
              maxWidth: '400px',
              padding: '12px',
              borderRadius: '8px',
              border: theme.glass.border,
              background: theme.background,
              color: theme.text.primary,
              fontSize: '16px',
              minHeight: '44px',
              cursor: 'pointer',
            }}
          >
            <option value="">-- Select a family --</option>
            {families.map((family) => (
              <option key={family.id} value={family.id}>
                {family.name} ({family.member_count || 0} members)
              </option>
            ))}
          </select>
        </div>

        {selectedFamily && (
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              onClick={() => loadRelationships(selectedFamily)}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: theme.glass.border,
                background: theme.card.background,
                color: theme.text.primary,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                minHeight: '44px',
              }}
            >
              🔄 Refresh
            </button>
            <button
              onClick={handleAddNewRelation}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: 'none',
                background: theme.accent.primary,
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                minHeight: '44px',
              }}
            >
              ➕ Add Relationship
            </button>
          </div>
        )}

        {/* Filter and Search */}
        {selectedFamily && allRelationships.length > 0 && (
          <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Relationship Type Filter */}
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: theme.text.secondary, 
                fontSize: '14px',
                fontWeight: 500,
              }}>
                Filter by Relationship Type
              </label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {[
                  { value: 'all', label: 'All Relationships', icon: '🔗' },
                  { value: 'parent-child', label: 'Parent-Child', icon: '👨‍👩‍👧‍👦' },
                  { value: 'spouse', label: 'Spouse', icon: '💑' },
                  { value: 'partner', label: 'Partner', icon: '🤝' },
                ].map((filter) => (
                  <button
                    key={filter.value}
                    onClick={() => setRelationshipFilter(filter.value)}
                    style={{
                      padding: '10px 16px',
                      borderRadius: '8px',
                      border: relationshipFilter === filter.value ? `2px solid ${theme.accent.primary}` : theme.glass.border,
                      background: relationshipFilter === filter.value ? theme.accent.primary + '20' : theme.card.background,
                      color: relationshipFilter === filter.value ? theme.accent.primary : theme.text.primary,
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: relationshipFilter === filter.value ? 600 : 500,
                      minHeight: '44px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <span>{filter.icon}</span>
                    <span>{filter.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Search Filter */}
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: theme.text.secondary, 
                fontSize: '14px',
                fontWeight: 500,
              }}>
                🔍 Search Relationships
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by person name or relationship type..."
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  padding: '12px',
                  borderRadius: '8px',
                  border: theme.glass.border,
                  background: theme.background,
                  color: theme.text.primary,
                  fontSize: '16px',
                  minHeight: '44px',
                }}
              />
              {(searchTerm || relationshipFilter !== 'all') && (
                <div style={{ marginTop: '8px', fontSize: '14px', color: theme.text.secondary }}>
                  Showing {filteredRelationships.length} of {allRelationships.length} relationships
                  {relationshipFilter !== 'all' && ` (filtered by ${relationshipFilter === 'parent-child' ? 'Parent-Child' : relationshipFilter === 'spouse' ? 'Spouse' : 'Partner'})`}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div style={{
          padding: '12px',
          borderRadius: '8px',
          background: 'rgba(255, 0, 0, 0.1)',
          border: '1px solid rgba(255, 0, 0, 0.3)',
          color: '#ff4444',
          marginBottom: '16px',
          fontSize: '14px',
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '32px 0', textAlign: 'center' }}>
          <p style={{ color: theme.text.secondary }}>Loading relationships...</p>
        </div>
      ) : !selectedFamily ? (
        <GlassCard style={{ padding: '32px', textAlign: 'center' }}>
          <p style={{ color: theme.text.secondary }}>Please select a family to view and manage relationships.</p>
        </GlassCard>
      ) : (
        <GlassCard style={{ padding: '16px', overflowX: 'auto' }}>
          {/* Add New Relationship Row */}
          {editingId === 'new' && (
            <div style={{
              padding: '16px',
              marginBottom: '16px',
              borderRadius: '8px',
              border: `2px solid ${theme.accent.primary}`,
              background: theme.card.background,
            }}>
              <h3 style={{ fontSize: '18px', marginBottom: '16px', color: theme.text.primary }}>
                Add New Relationship
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: theme.text.secondary, fontSize: '14px' }}>
                    Relationship Type
                  </label>
                  <select
                    value={editForm.type || 'parent-child'}
                    onChange={(e) => {
                      setEditForm({ ...editForm, type: e.target.value });
                      setEditingType(e.target.value);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: theme.glass.border,
                      background: theme.background,
                      color: theme.text.primary,
                      fontSize: '16px',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="parent-child">Parent-Child</option>
                    <option value="partnership">Spouse/Partner Relationship</option>
                  </select>
                </div>

                {editForm.type === 'parent-child' ? (
                  <>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', color: theme.text.secondary, fontSize: '14px' }}>
                        Parent
                      </label>
                      <SearchablePersonSelect
                        value={editForm.parent_person_id}
                        onChange={(personId) => setEditForm({ ...editForm, parent_person_id: personId })}
                        options={members}
                        placeholder="Search for parent..."
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', color: theme.text.secondary, fontSize: '14px' }}>
                        Child
                      </label>
                      <SearchablePersonSelect
                        value={editForm.child_person_id}
                        onChange={(personId) => setEditForm({ ...editForm, child_person_id: personId })}
                        options={members}
                        placeholder="Search for child..."
                        excludeId={editForm.parent_person_id}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', color: theme.text.secondary, fontSize: '14px' }}>
                        Relationship Kind
                      </label>
                      <select
                        value={editForm.kind || 'biological'}
                        onChange={(e) => setEditForm({ ...editForm, kind: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '12px',
                          borderRadius: '8px',
                          border: theme.glass.border,
                          background: theme.background,
                          color: theme.text.primary,
                          fontSize: '16px',
                          cursor: 'pointer',
                        }}
                      >
                        <option value="biological">Biological</option>
                        <option value="adoptive">Adoptive</option>
                        <option value="step">Step</option>
                        <option value="guardian">Guardian</option>
                        <option value="unknown">Unknown</option>
                      </select>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', color: theme.text.secondary, fontSize: '14px' }}>
                        Person 1
                      </label>
                      <SearchablePersonSelect
                        value={editForm.person_id_1}
                        onChange={(personId) => setEditForm({ ...editForm, person_id_1: personId })}
                        options={members}
                        placeholder="Search for person..."
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', color: theme.text.secondary, fontSize: '14px' }}>
                        Person 2
                      </label>
                      <SearchablePersonSelect
                        value={editForm.person_id_2}
                        onChange={(personId) => setEditForm({ ...editForm, person_id_2: personId })}
                        options={members}
                        placeholder="Search for person..."
                        excludeId={editForm.person_id_1}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', color: theme.text.secondary, fontSize: '14px' }}>
                        Partnership Type
                      </label>
                      <select
                        value={editForm.partnership_type || 'spouse'}
                        onChange={(e) => setEditForm({ ...editForm, partnership_type: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '12px',
                          borderRadius: '8px',
                          border: theme.glass.border,
                          background: theme.background,
                          color: theme.text.primary,
                          fontSize: '16px',
                          cursor: 'pointer',
                        }}
                      >
                        <option value="spouse">Spouse</option>
                        <option value="partner">Partner</option>
                      </select>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', color: theme.text.secondary, fontSize: '14px' }}>
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={editForm.start_date || ''}
                          onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '8px',
                            border: theme.glass.border,
                            background: theme.background,
                            color: theme.text.primary,
                            fontSize: '16px',
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', color: theme.text.secondary, fontSize: '14px' }}>
                          End Date
                        </label>
                        <input
                          type="date"
                          value={editForm.end_date || ''}
                          onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '8px',
                            border: theme.glass.border,
                            background: theme.background,
                            color: theme.text.primary,
                            fontSize: '16px',
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={handleSave}
                    style={{
                      padding: '12px 20px',
                      borderRadius: '8px',
                      border: 'none',
                      background: theme.accent.primary,
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 600,
                      minHeight: '44px',
                    }}
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancel}
                    style={{
                      padding: '12px 20px',
                      borderRadius: '8px',
                      border: theme.glass.border,
                      background: 'transparent',
                      color: theme.text.secondary,
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 600,
                      minHeight: '44px',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Relationships Table */}
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            minWidth: '1000px',
          }}>
            <thead>
              <tr style={{ borderBottom: theme.glass.border }}>
                <th style={{ padding: '12px', textAlign: 'left', color: theme.text.secondary, fontSize: '14px', fontWeight: 600 }}>
                  Type
                </th>
                <th style={{ padding: '12px', textAlign: 'left', color: theme.text.secondary, fontSize: '14px', fontWeight: 600 }}>
                  Parent / Person 1
                </th>
                <th style={{ padding: '12px', textAlign: 'left', color: theme.text.secondary, fontSize: '14px', fontWeight: 600 }}>
                  Child / Person 2
                </th>
                <th style={{ padding: '12px', textAlign: 'left', color: theme.text.secondary, fontSize: '14px', fontWeight: 600 }}>
                  Details
                </th>
                <th style={{ padding: '12px', textAlign: 'left', color: theme.text.secondary, fontSize: '14px', fontWeight: 600 }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredRelationships.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ padding: '32px', textAlign: 'center', color: theme.text.secondary }}>
                    {searchTerm ? `No relationships found matching "${searchTerm}"` : 'No relationships found. Click "Add Relationship" to create one.'}
                  </td>
                </tr>
              ) : (
                filteredRelationships.map((relation) => {
                  const isEditing = editingId === relation._id;
                  return (
                    <tr
                      key={relation._id}
                      style={{
                        borderBottom: theme.glass.border,
                        background: isEditing ? theme.card.background : 'transparent',
                        transition: 'background 0.2s ease',
                      }}
                    >
                      {isEditing ? (
                        <>
                          <td style={{ padding: '12px' }}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              background: relation._type === 'parent-child' 
                                ? theme.accent.primary + '20' 
                                : relation.partnership_type === 'spouse'
                                  ? '#ff6b6b' + '20'
                                  : '#9b59b6' + '20',
                              color: relation._type === 'parent-child' 
                                ? theme.accent.primary 
                                : relation.partnership_type === 'spouse'
                                  ? '#ff6b6b'
                                  : '#9b59b6',
                              fontSize: '12px',
                              fontWeight: 600,
                            }}>
                              {relation._type === 'parent-child' 
                                ? 'Parent-Child' 
                                : relation.partnership_type === 'spouse'
                                  ? '💑 Spouse'
                                  : '🤝 Partner'}
                            </span>
                          </td>
                          <td style={{ padding: '12px' }}>
                            {relation._type === 'parent-child' ? (
                              <div style={{ 
                                padding: '8px 12px', 
                                borderRadius: '4px', 
                                background: theme.background,
                                border: theme.glass.border,
                                color: theme.text.secondary,
                                fontSize: '14px',
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                  <span>{getParentRoleIcon(editForm.parent_person_id)}</span>
                                  <span style={{ fontWeight: 500 }}>{getPersonName(editForm.parent_person_id)}</span>
                                </div>
                                <div style={{ fontSize: '11px', marginTop: '4px', color: theme.text.muted }}>
                                  {getParentRole(editForm.parent_person_id)}
                                </div>
                                {getPerson(editForm.parent_person_id)?.sex === 'unknown' && (
                                  <div style={{ 
                                    fontSize: '10px', 
                                    color: '#ff9800',
                                    marginTop: '4px',
                                    padding: '4px',
                                    borderRadius: '4px',
                                    background: '#ff980020',
                                  }}>
                                    ⚠️ Sex unknown -{' '}
                                    <Link 
                                      to={`/people/${editForm.parent_person_id}`}
                                      target="_blank"
                                      style={{ 
                                        color: '#ff9800', 
                                        textDecoration: 'underline',
                                        fontWeight: 600,
                                      }}
                                    >
                                      edit member
                                    </Link>
                                    {' '}to set
                                  </div>
                                )}
                                <div style={{ fontSize: '11px', marginTop: '4px', color: theme.text.muted }}>
                                  (Cannot change when editing)
                                </div>
                              </div>
                            ) : (
                              <div style={{ 
                                padding: '8px 12px', 
                                borderRadius: '4px', 
                                background: theme.background,
                                border: theme.glass.border,
                                color: theme.text.secondary,
                                fontSize: '14px',
                              }}>
                                {getPersonName(editForm.person_id_1)}
                                <div style={{ fontSize: '11px', marginTop: '4px', color: theme.text.muted }}>
                                  (Cannot change when editing)
                                </div>
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '12px' }}>
                            {relation._type === 'parent-child' ? (
                              <div style={{ 
                                padding: '8px 12px', 
                                borderRadius: '4px', 
                                background: theme.background,
                                border: theme.glass.border,
                                color: theme.text.secondary,
                                fontSize: '14px',
                              }}>
                                {getPersonName(editForm.child_person_id)}
                                <div style={{ fontSize: '11px', marginTop: '4px', color: theme.text.muted }}>
                                  (Cannot change when editing)
                                </div>
                              </div>
                            ) : (
                              <div style={{ 
                                padding: '8px 12px', 
                                borderRadius: '4px', 
                                background: theme.background,
                                border: theme.glass.border,
                                color: theme.text.secondary,
                                fontSize: '14px',
                              }}>
                                {getPersonName(editForm.person_id_2)}
                                <div style={{ fontSize: '11px', marginTop: '4px', color: theme.text.muted }}>
                                  (Cannot change when editing)
                                </div>
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '12px' }}>
                            {relation._type === 'parent-child' ? (
                              <select
                                value={editForm.kind || 'biological'}
                                onChange={(e) => setEditForm({ ...editForm, kind: e.target.value })}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  borderRadius: '4px',
                                  border: theme.glass.border,
                                  background: theme.background,
                                  color: theme.text.primary,
                                  fontSize: '14px',
                                  cursor: 'pointer',
                                }}
                              >
                                <option value="biological">Biological</option>
                                <option value="adoptive">Adoptive</option>
                                <option value="step">Step</option>
                                <option value="guardian">Guardian</option>
                                <option value="unknown">Unknown</option>
                              </select>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <select
                                  value={editForm.partnership_type || 'spouse'}
                                  onChange={(e) => setEditForm({ ...editForm, partnership_type: e.target.value })}
                                  style={{
                                    width: '100%',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: theme.glass.border,
                                    background: theme.background,
                                    color: theme.text.primary,
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  <option value="spouse">Spouse</option>
                                  <option value="partner">Partner</option>
                                </select>
                                <input
                                  type="date"
                                  value={editForm.start_date || ''}
                                  onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                                  placeholder="Start date"
                                  style={{
                                    width: '100%',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: theme.glass.border,
                                    background: theme.background,
                                    color: theme.text.primary,
                                    fontSize: '14px',
                                  }}
                                />
                                <input
                                  type="date"
                                  value={editForm.end_date || ''}
                                  onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
                                  placeholder="End date"
                                  style={{
                                    width: '100%',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: theme.glass.border,
                                    background: theme.background,
                                    color: theme.text.primary,
                                    fontSize: '14px',
                                  }}
                                />
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '12px' }}>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button
                                onClick={handleSave}
                                style={{
                                  padding: '6px 12px',
                                  borderRadius: '4px',
                                  border: 'none',
                                  background: theme.accent.primary,
                                  color: 'white',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                }}
                              >
                                ✓
                              </button>
                              <button
                                onClick={handleCancel}
                                style={{
                                  padding: '6px 12px',
                                  borderRadius: '4px',
                                  border: theme.glass.border,
                                  background: 'transparent',
                                  color: theme.text.secondary,
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={{ padding: '12px' }}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              background: relation._type === 'parent-child' 
                                ? theme.accent.primary + '20' 
                                : relation.partnership_type === 'spouse'
                                  ? '#ff6b6b' + '20'
                                  : '#9b59b6' + '20',
                              color: relation._type === 'parent-child' 
                                ? theme.accent.primary 
                                : relation.partnership_type === 'spouse'
                                  ? '#ff6b6b'
                                  : '#9b59b6',
                              fontSize: '12px',
                              fontWeight: 600,
                            }}>
                              {relation._type === 'parent-child' 
                                ? 'Parent-Child' 
                                : relation.partnership_type === 'spouse'
                                  ? '💑 Spouse'
                                  : '🤝 Partner'}
                            </span>
                          </td>
                          <td style={{ padding: '12px', color: theme.text.primary, fontSize: '14px' }}>
                            {relation._type === 'parent-child' ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span>{getParentRoleIcon(relation.parent_person_id)}</span>
                                  <span style={{ fontWeight: 500 }}>{getPersonName(relation.parent_person_id)}</span>
                                </div>
                                <div style={{ 
                                  fontSize: '12px', 
                                  color: getPerson(relation.parent_person_id)?.sex === 'unknown' ? '#ff9800' : theme.text.secondary,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}>
                                  <span>{getParentRole(relation.parent_person_id)}</span>
                                  {getPerson(relation.parent_person_id)?.sex === 'unknown' && (
                                    <span title="Parent's sex is not set. Please edit the member's information to set their sex.">
                                      ⚠️
                                    </span>
                                  )}
                                </div>
                              </div>
                            ) : (
                              getPersonName(relation.person_id_1)
                            )}
                          </td>
                          <td style={{ padding: '12px', color: theme.text.primary, fontSize: '14px' }}>
                            {relation._type === 'parent-child' 
                              ? getPersonName(relation.child_person_id)
                              : getPersonName(relation.person_id_2)}
                          </td>
                          <td style={{ padding: '12px', color: theme.text.secondary, fontSize: '14px' }}>
                            {relation._type === 'parent-child' ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span style={{ textTransform: 'capitalize', fontWeight: 500 }}>
                                  {relation.kind || 'unknown'}
                                </span>
                                {getPerson(relation.parent_person_id)?.sex === 'unknown' && (
                                  <div style={{ 
                                    fontSize: '11px', 
                                    color: '#ff9800',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    background: '#ff980020',
                                    display: 'inline-block',
                                    width: 'fit-content',
                                  }}>
                                    ⚠️ Parent sex unknown - cannot determine Father/Mother. 
                                    <Link 
                                      to={`/people/${relation.parent_person_id}`}
                                      target="_blank"
                                      style={{ 
                                        marginLeft: '4px', 
                                        color: '#ff9800', 
                                        textDecoration: 'underline',
                                        fontWeight: 600,
                                      }}
                                    >
                                      Edit Member
                                    </Link>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div>
                                <div style={{ textTransform: 'capitalize', marginBottom: '4px' }}>
                                  {relation.partnership_type || 'spouse'}
                                </div>
                                {relation.start_date && (
                                  <div style={{ fontSize: '12px' }}>
                                    Start: {new Date(relation.start_date).toLocaleDateString()}
                                  </div>
                                )}
                                {relation.end_date && (
                                  <div style={{ fontSize: '12px' }}>
                                    End: {new Date(relation.end_date).toLocaleDateString()}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '12px' }}>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button
                                onClick={() => handleEdit(relation, relation._type)}
                                style={{
                                  padding: '8px 12px',
                                  borderRadius: '6px',
                                  border: theme.glass.border,
                                  background: 'transparent',
                                  color: theme.text.secondary,
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  minHeight: '44px',
                                  minWidth: '44px',
                                }}
                                title="Edit relationship"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => handleDelete(relation, relation._type)}
                                style={{
                                  padding: '8px 12px',
                                  borderRadius: '6px',
                                  border: theme.glass.border,
                                  background: 'rgba(255, 0, 0, 0.1)',
                                  color: '#ff4444',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  minHeight: '44px',
                                  minWidth: '44px',
                                }}
                                title="Delete relationship"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </GlassCard>
      )}
    </div>
  );
};

export default RelationManagement;
